import * as angular from 'angular';
import { find } from 'lodash';
import { ModalTheme, ModalType } from '../modal.service';

/**
 * @todo (Franklin) 10/26/2020: Separate interfaces for directive, divider,
 *    modal, etc. https://github.com/mode/issues/issues/6334
 */
export interface SelectOption {
  confirm?: {
    buttonCancel?: string;
    buttonConfirm?: string;
    content: string;
    modalType: ModalType;
    modalTheme: ModalTheme;
    title: string;
  };
  directive?: {
    name: SelectionOptionDirective;
    attrs?: {
      [key: string]: any;
    };
  };
  label: string;
  labelCaption?: string;
  value: string | number;
  isDivider?: boolean;
  isDataset?: boolean;
  isValid?: (param: any) => boolean;
}

export enum SelectionOptionDirective {
  MoveSpace = 'move-space',
  RemoveShortcutSpace = 'remove-shortcut-space',
  ShortcutSpace = 'shortcut-space',
  SpaceReportPin = 'space-report-pin',
  DeleteReport = 'mode-delete-report',
}

export class SelectFieldController {
  static $inject = [];

  public selected!: SelectOption | null;
  public options!: SelectOption[];
  public placeholder!: string;
  public labelPrefix!: string;
  public onChange!: (args: { $option: SelectOption }) => void;

  constructor() {}

  $onInit(): void {
    this.selected = this.selected || null;
    this.options = this.options || [];
    this.placeholder = this.placeholder || 'Select...';
  }

  $onChanges(changes: angular.IOnChangesObject): void {
    if (changes['options']) {
      if (!this.validateOption(this.selected, changes['options'].currentValue)) {
        this.setSelected(null);
      }
    }
  }

  validateOption(option: SelectOption | null, options: SelectOption[] = this.options): boolean {
    return !!(option && find(options, option));
  }

  updateSelected(option: SelectOption): void {
    if (this.validateOption(option)) {
      this.setSelected(option);
    } else {
      this.setSelected(null);
    }
  }

  setSelected(option: SelectOption | null): void {
    this.selected = option;
  }

  getControlLabel(): SelectOption['label'] {
    return (this.selected && this.selected.label) || this.placeholder;
  }

  handleControlClick(): void {}

  handleMenuItemSelection(value: SelectOption['value']): void {
    const $option: SelectOption | undefined = find(this.options, { value });

    if ($option && this.validateOption($option)) {
      // optimistically update selected option state
      this.updateSelected($option);
      // invoke callback
      this.onChange({ $option });
    }
  }

  isSelected(option: SelectOption): boolean {
    return !!this.selected && this.selected.value === option.value;
  }
}

const SelectFieldComponent: angular.IComponentOptions = {
  controller: SelectFieldController,
  controllerAs: 'ctrl',
  transclude: {
    control: '?selectFieldControl',
  },
  bindings: {
    inputId: '@',
    inputName: '@',
    labelPrefix: '@',
    onChange: '&',
    options: '<',
    selected: '<',
    title: '@',
  },
  template: `
    <div uib-dropdown
         class="select-field"
         ng-class="{'has-no-selection': !ctrl.selected}">
      <a uib-dropdown-toggle
         ng-click="ctrl.handleControlClick($event)"
         ng-transclude="control">
        <span class="select-field-control form-control icon-caret">
          <span class="prefix-label">{{ctrl.labelPrefix}}</span> {{ctrl.getControlLabel()}}
        </span>
      </a>
      <ul uib-dropdown-menu
          class="select-field-menu">
        <li class="menu-title" ng-if="ctrl.title">
          {{ ctrl.title }}
        </li>
        <select-field-menu-item
          ng-repeat="option in ctrl.options"
          confirm="option.confirm"
          label="option.label"
          label-caption="option.labelCaption"
          on-select="ctrl.handleMenuItemSelection($value)"
          selected="ctrl.isSelected(option)"
          value="option.value"
        />
      </ul>
      <input
        ng-if="(ctrl.inputId || ctrl.inputName) && ctrl.selected"
        id="{{ ctrl.inputId }}"
        name="{{ ctrl.inputName }}"
        type="hidden"
        value="{{ ctrl.selected.value }}"
      />
    </div>
  `,
};

export { SelectFieldComponent };
